import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../data/redux/store";
import { message } from "antd";

/** === IMPORT ALL MASTER ACTIONS HERE === */

// Department
import { createDepartment, deleteDepartment, fetchAllDepartments } from "../data/redux/department/departmentAction";
import { getUserById, updateUser } from "../data/redux/user/userAction";


// Example future masters:
// import { createDesignation } from "../core/data/redux/designation/designationAction";
// import { createProject } from "../core/data/redux/project/projectAction";


export default function GlobalMasterListener() {
    const dispatch = useDispatch<AppDispatch>();
    const user = useSelector((state: any) => state.auth.user);
    const departments = useSelector((state: any) => state.department.departments);

    /** 
     * Helper: Find department by name (case-insensitive)
     */
    const getDepartmentIdByName = (name: string) => {
        const match = departments.find(
            (d: any) => d.Name.toLowerCase() === name.toLowerCase()
        );
        return match?.Id || null;
    };


    // ---------------------------------------------------
    //                AI Create Department
    // ---------------------------------------------------
    useEffect(() => {
        const handler = async (event: any) => {
            const { name, status } = event.detail;

            const data = {
                Name: name,
                Description: "",
                Status: status || "active",
                hrId: [],
                CreatedBy: user?.UserId,
                ModifiedBy: user?.UserId,
            };

            const result: any = await dispatch(createDepartment(data));

            if (result.meta.requestStatus === "fulfilled") {
                message.success(`Department "${name}" created successfully`);
                dispatch(fetchAllDepartments());
            } else {
                message.error(result.payload?.error || "Failed to create department");
            }
        };

        window.addEventListener("ai-create-department", handler);
        return () => window.removeEventListener("ai-create-department", handler);
    }, [dispatch, user, departments]);


    // ---------------------------------------------------
    //                AI Delete Department
    // ---------------------------------------------------
    useEffect(() => {
        const handler = async (event: any) => {
            const { name } = event.detail;

            const departmentId = getDepartmentIdByName(name);

            if (!departmentId) {
                message.error(`Department "${name}" not found`);
                return;
            }

            const result: any = await dispatch(deleteDepartment({ departmentId }));

            if (result.meta.requestStatus === "fulfilled") {
                message.success(`Department "${name}" deleted`);
                dispatch(fetchAllDepartments());
            } else {
                message.error(result.payload?.error || "Delete failed");
            }
        };

        window.addEventListener("ai-delete-department", handler);
        return () => window.removeEventListener("ai-delete-department", handler);
    }, [dispatch, departments]);


    // ---------------------------------------------------
    //            Add More Master Listeners Here
    // ---------------------------------------------------

    /**
     * Example: Create Designation
     * --------------------------------------
     * window.addEventListener("ai-create-designation", async (event: any) => {
     *      const { name } = event.detail;
     *      await dispatch(createDesignation({ Name: name }));
     * });
     */

    useEffect(() => {
        const handler = async (event: any) => {
            const file = event.detail.file;
            const { field, value } = event.detail;

            // Always include existing values so nothing becomes undefined
            let payload: any = {
                DisplayName: user.DisplayName,
                mobile: user.mobile,
                image: file    // optional
            };

            // Override only the changed field
            if (field === "name") payload.DisplayName = value;
            if (field === "mobile") payload.mobile = value;

            try {
                const result: any = await dispatch(updateUser({
                    userId: user.UserId,
                    userData: payload
                }));

                if (result.meta.requestStatus === "fulfilled") {
                    message.success("Profile updated successfully!");
                    dispatch(getUserById(user.UserId));
                } else {
                    message.error("Failed to update profile.");
                }
            } catch (error) {
                message.error("Something went wrong!");
            }
        };

        window.addEventListener("ai-update-profile", handler);
        return () => window.removeEventListener("ai-update-profile", handler);
    }, [dispatch, user]);

    // ---------------------------------------------------
    //            AI File Upload (Profile Photo)
    // ---------------------------------------------------
    useEffect(() => {
        const handler = async (event: any) => {
            const file = event.detail.file;

            if (!file) return;

            let payload: any = {
                DisplayName: user.DisplayName,
                mobile: user.mobile,
                image: file
            };

            try {
                const result: any = await dispatch(updateUser({
                    userId: user.UserId,
                    userData: payload
                }));

                if (result.meta.requestStatus === "fulfilled") {
                    message.success("Profile photo updated!");
                    dispatch(getUserById(user.UserId));
                } else {
                    message.error("Failed to update photo");
                }
            } catch (error) {
                message.error("Something went wrong while uploading photo");
            }
        };

        window.addEventListener("ai-file-upload", handler);
        return () => window.removeEventListener("ai-file-upload", handler);
    }, [dispatch, user]);

    return null;
}
